import {Navigate} from 'react-router-dom';
import {
    FetchError,
    Loading,
} from '../../../components';
import {useQuery} from '../../../hooks';
import {Routes} from '../../../Routes';
import {
    Endpoint,
    extractErrorMessage,
} from '../../../util';
import {PhysicianReviewStatus} from './KitActivationGuard';

export class ReorderState {
    static NOT_REQUIRED_OR_PLACED = 'NOT_REQUIRED_OR_PLACED';

    static REQUIRED = 'REQUIRED';

    static validate(value) {
        return [this.NOT_REQUIRED_OR_PLACED, this.REQUIRED].includes(value);
    }
}

export function ReorderTestGuard(props) {
    const {children, state} = props;

    const {
        data: {physicianReviewStatus = PhysicianReviewStatus.NONE} = {},
        error,
        isError,
        isPending,
    } = useQuery({endpoint: Endpoint.SUMMARY});

    if (!ReorderState.validate(state)) {
        throw new Error(`Unknown ReorderState - ${state}`);
    }

    if (isError) {
        return (
            <div
                className={'guard'}
                key={'error'}
            >
                <FetchError reason={extractErrorMessage(error)} />
            </div>
        );
    }

    if (isPending) {
        return (
            <div
                className={'guard'}
                key={'loading'}
            >
                <Loading key={'loading'} />
            </div>
        );
    }

    // The order is closed/expired with no results and no reorder invite sent, so there is nothing to reorder into
    if (physicianReviewStatus === PhysicianReviewStatus.UNAVAILABLE) {
        return (
            <Navigate
                replace={true}
                to={Routes.TEST_ORDER_UNAVAILABLE}
            />
        );
    }

    // You should only be able to access the requested page if the user has been sent an invite to reorder a test
    if (state === ReorderState.REQUIRED) {
        if (physicianReviewStatus === PhysicianReviewStatus.REORDER_INVITE_SENT) {
            return children;
        }

        // If they haven't been invited to reorder their expired test, send them to the /home route
        return (
            <Navigate
                replace={true}
                to={Routes.HOME}
            />
        );
    }

    // You shouldn't be able to access the requested page if a reorder test invite has been sent
    if (physicianReviewStatus === PhysicianReviewStatus.REORDER_INVITE_SENT) {
        return (
            <Navigate
                replace={true}
                to={Routes.TEST_REORDER}
            />
        );
    }

    // A test reorder is either not required, or has been placed
    return children;
}
